import { useMemo } from 'react';
import { type LocationState } from 'redux-first-router';
import { connect } from 'react-redux';
import ExploreIdeas from '@/pages/DiscoverHub/components/ExploreIdeas';
import {
  DiscoverTabs,
  ScreenerCallToAction,
} from '@/components/Nav/DiscoverTabs';
import { Footer } from '@/components/Footer';
import DocumentHeader from '@/components/DocumentHeader';
import { SWS_HOST } from '@/constants';
import { getPath } from '@/router/utils';
import {
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_DISCOVER_INVESTING_IDEAS_MARKET,
} from '@/constants/routes';
import { getReadableCountry } from '@/utilities/markets';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { useHrefLangMarkets } from '@/hooks/discover/useHrefLangMarkets';
import { MarketIndustryFilter } from '@/components/Header/MarketIndustryFilter';
import { Heading } from '@simplywallst/ui-core/next';
import { useMarketFilter } from './hooks/useMarketFilter';
import { SwitchMarketButton } from './components/SwitchMarketButton';

export interface Props {
  route: string;
  market?: PrimaryCountryISOElseGlobal;
}

const mapState = (state): Props => {
  const location = state.location as LocationState;
  return {
    route: location.type,
    market: location.payload.market,
  };
};

type BaseDiscoverHubProps = ReturnType<typeof mapState>;

const useMetaData = (market: PrimaryCountryISOElseGlobal, route: string) => {
  const [country, countryDemonym] = useMemo(
    () => [
      getReadableCountry(market, 'label'),
      getReadableCountry(market, 'demonym'),
    ],
    [market]
  );
  const { hrefLangMarkets } = useHrefLangMarkets(route);

  const baseMetaData = {
    allowCrawlers: true, // Allow crawlers or hreflang tags will not work correctly
    hrefLangs: hrefLangMarkets,
  };

  if (market === 'global') {
    return {
      ...baseMetaData,
      title: 'Discover - Investing Ideas',
      description: `Discover curated investment ideas and opportunities from around the world. Find undervalued stocks, growth stocks, and emerging market opportunities across global markets.`,
      pageTitle: 'Investing Ideas',
      pageDescription:
        'Explore investing ideas for any market cycle in the global stock market. From fundamental screening to industry themes and niche opportunities, find stocks with strong financials, growth potential, and strategic advantages.',
      canonical: `${SWS_HOST}${getPath({
        type: ROUTE_DISCOVER_INVESTING_IDEAS,
        payload: {},
      })}`,
    };
  }

  return {
    ...baseMetaData,
    title: `Discover ${countryDemonym} Investing Ideas`,
    description: `Discover curated investment ideas and opportunities in ${country}. Find undervalued stocks, growth stocks, and emerging market opportunities tailored for ${countryDemonym} investors.`,
    pageTitle: `${countryDemonym} Investing Ideas`,
    pageDescription: `Explore investing ideas for any market cycle in the ${countryDemonym} stock market. From fundamental screening to industry themes and niche opportunities, find stocks with strong financials, growth potential, and strategic advantages.`,
    canonical: `${SWS_HOST}${getPath({
      type: ROUTE_DISCOVER_INVESTING_IDEAS_MARKET,
      payload: { market },
    })}`,
  };
};

const BaseDiscoverHub = ({
  route,
  market = 'global',
}: BaseDiscoverHubProps) => {
  const meta = useMetaData(market, route);
  const { marketOptions, handleMarketChange } = useMarketFilter({ route });

  return (
    <DiscoverTabs route={route} market={market}>
      <DocumentHeader
        title={meta.title}
        description={meta.description}
        allowCrawlers={meta.allowCrawlers}
      >
        {meta.allowCrawlers && <link rel="canonical" href={meta.canonical} />}
        {meta.hrefLangs && (
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${SWS_HOST}${getPath({
              // Technically incorrect global page for "logged in" users but it's fine
              type: ROUTE_DISCOVER_INVESTING_IDEAS,
              payload: {},
            })}`}
          />
        )}
        {meta.hrefLangs}
      </DocumentHeader>
      <header className="px-4 py-2 lg:px-0 lg:pb-1 lg:pt-3">
        <MarketIndustryFilter
          marketOptions={marketOptions}
          selectedMarket={market}
          onMarketChange={handleMarketChange}
          hideIndustryFilter={true}
        >
          {market === 'global' && <SwitchMarketButton />}
        </MarketIndustryFilter>
        <div className="grid gap-4 py-3 lg:py-5">
          <Heading size="mega">{meta.pageTitle}</Heading>
          {meta.pageDescription && (
            <div className="text-pretty text-xs text-softer md:max-w-[66%]">
              {meta.pageDescription}
            </div>
          )}
          <div className="lg:hidden">
            <ScreenerCallToAction />
          </div>
        </div>
      </header>
      <ExploreIdeas market={market} />
      <Footer />
    </DiscoverTabs>
  );
};

export default connect(mapState)(BaseDiscoverHub);
